import documentPreview from 'src/modules/3rd-party-management/assets/images/carrier-guard/community-licence-scan.png';

import { getCarrierPublicProfile } from '../../../../data/carrierPublicProfiles';
import { getEuropeanCarrierById } from '../../../../data/europeanCarriers';
import createCompanyReports from '../../../../services/createCompanyReports';
import CarrierGuardFreightDetailsService from '../../services/CarrierGuardFreightDetailsService';
import {
    addDemoMinutes,
    formatDemoDateTime,
    parseDemoDateTime,
} from '../../../../data/mockDemoDates';
import createCallVerification from './createCallVerification';
import createDocumentForensics from './createDocumentForensics';

const verificationSteps = {
    verified: [
        { stage: 'Documents', state: 'verified' },
        { stage: 'Company', state: 'verified' },
        { stage: 'Digital Identity', state: 'verified' },
        { stage: 'Human Identity', state: 'verified' },
        { stage: 'Gate Security', state: 'verified' },
    ],
    rejected: [
        { stage: 'Documents', state: 'issues' },
        { stage: 'Company', state: 'issues' },
        { stage: 'Digital Identity', state: 'issues' },
        { stage: 'Human Identity', state: 'not_completed' },
        { stage: 'Gate Security', state: 'not_completed' },
    ],
    actionRequired: [
        { stage: 'Documents', state: 'completed' },
        { stage: 'Company', state: 'completed' },
        { stage: 'Digital Identity', state: 'in_review' },
        { stage: 'Human Identity', state: 'not_started' },
        { stage: 'Gate Security', state: 'not_started' },
    ],
    verifying: [
        { stage: 'Documents', state: 'completed' },
        { stage: 'Company', state: 'in_review' },
        { stage: 'Digital Identity', state: 'not_started' },
        { stage: 'Human Identity', state: 'not_started' },
        { stage: 'Gate Security', state: 'not_started' },
    ],
};

const bidSummaries = {
    1: {
        country: 'AT',
        established: 2015,
        deliveryDays: 2,
        duration: '13 minutes',
        outcome: {
            status: 'Verified',
            description: 'The submitted carrier profile has successfully passed all verification checks. Company data, documents, digital identity, and contact verification are consistent and valid. No high-risk indicators were found.',
            metrics: [
                { label: 'Risk level', value: 'Low', type: 'risk' },
                { label: 'Total checked items', value: 18 },
                { label: 'Red flags', value: 0 },
                { label: 'Recommendation', value: 'Proceed', className: 'text-success' },
            ],
        },
        steps: verificationSteps.verified,
        highlightsTitle: 'Verification highlights',
        attentionItems: [],
        otherChecks: [
            { id: 1, title: 'Valid business registration', description: 'The submitted company data matches the relevant official registry.', status: 'Verified', icon: 'ri-file-text-line' },
            { id: 2, title: 'Directors and ownership verified', description: 'Directors and UBO information match official records.', status: 'Verified', icon: 'ri-group-line' },
            { id: 3, title: 'Contact information confirmed', description: 'Phone number and email address are active and match company records.', status: 'Verified', icon: 'ri-phone-line' },
            { id: 4, title: 'Legitimate digital identity', description: 'Website, domain, and email infrastructure are consistent and trusted.', status: 'Verified', icon: 'ri-global-line' },
            { id: 5, title: 'No adverse records found', description: 'No sanctions, PEP, or negative media matches detected.', status: 'Verified', icon: 'ri-shield-star-line' },
        ],
        documents: [
            { id: 1, title: 'Commercial Register', status: 'Validated', preview: documentPreview },
            { id: 2, title: 'Carrier License', status: 'Valid', preview: documentPreview },
            { id: 3, title: 'Insurance Certificate', status: 'Valid', preview: documentPreview },
            { id: 4, title: 'ID Document', status: 'Verified', preview: documentPreview },
        ],
        showRequestInformation: false,
        showAddDocument: false,
    },
    2: {
        country: 'DE',
        established: 2012,
        deliveryDays: 2,
        duration: '15 minutes',
        outcome: {
            status: 'Rejected',
            description: 'The submitted carrier profile did not pass the demo verification checks. Multiple inconsistencies were detected in the submitted company, contact, and identity information.',
            metrics: [
                { label: 'Risk level', value: 'High', type: 'risk' },
                { label: 'Total red flags', value: 5, className: 'text-danger' },
                { label: 'Recommendation', value: 'Do not proceed', className: 'text-danger' },
            ],
        },
        steps: verificationSteps.rejected,
        highlightsTitle: 'Key reasons for rejection',
        attentionItems: [
            { id: 1, title: 'Submitted registry details not matched', description: 'The submitted registration details could not be matched automatically against the relevant official registry.', status: 'Critical', icon: 'ri-building-line' },
            { id: 2, title: 'Director identity could not be verified', description: 'The listed director does not match official records.', status: 'High', icon: 'ri-user-unfollow-line' },
            { id: 3, title: 'Invalid contact information', description: 'Phone number is not in service and email domain is newly created.', status: 'High', icon: 'ri-phone-line' },
            { id: 4, title: 'Suspicious website', description: 'The website was registered 2 weeks ago and shows signs of a template site.', status: 'Medium', icon: 'ri-global-line' },
            { id: 5, title: 'Inconsistent document information', description: 'Address on the provided insurance certificate does not match company records.', status: 'Medium', icon: 'ri-file-text-line' },
        ],
        otherChecks: [],
        documents: [
            { id: 4, title: 'Commercial Register', status: 'Validated', preview: documentPreview },
            { id: 1, title: 'Insurance Certificate', status: 'Inconsistent data', preview: documentPreview },
            { id: 2, title: 'Carrier License', status: 'Invalid document', preview: documentPreview },
            { id: 3, title: 'ID Document', status: 'Not matching', preview: documentPreview },
        ],
        showRequestInformation: false,
        showAddDocument: false,
    },
    3: {
        country: 'RO',
        established: 2018,
        deliveryDays: 3,
        estimatedCompletion: '~ 5 minutes',
        outcome: {
            status: 'Action Required',
            description: 'Our verification detected some missing or inconsistent information. Please review the details below and request additional information from the carrier to continue the verification process.',
            metrics: [
                { label: 'Risk level', value: 'Medium', type: 'risk' },
                { label: 'Completed checks', value: '3 / 5' },
                { label: 'Outstanding items', value: 2, className: 'text-warning' },
                { label: 'Recommendation', value: 'Request information', className: 'text-warning' },
            ],
        },
        steps: verificationSteps.actionRequired,
        highlightsTitle: 'Items requiring your attention',
        attentionItems: [
            { id: 1, title: 'Missing director information', description: 'Director identity could not be verified. Please request a copy of ID or official record.', status: 'Required', icon: 'ri-user-unfollow-line' },
            { id: 2, title: 'Unverified phone number', description: 'Phone number is not reachable. Please ask the carrier to confirm a valid business number.', status: 'Needs info', icon: 'ri-phone-line' },
        ],
        otherChecks: [
            { id: 3, title: 'Business registration', description: 'The submitted company data matches the relevant official registry.', status: 'Verified', icon: 'ri-checkbox-circle-fill' },
            { id: 4, title: 'Website and domain', description: 'Website is active and consistent with company details.', status: 'Verified', icon: 'ri-checkbox-circle-fill' },
            { id: 5, title: 'Documents', description: 'Insurance certificate and carrier license are valid.', status: 'Verified', icon: 'ri-checkbox-circle-fill' },
        ],
        documents: [
            { id: 1, title: 'Carrier License', status: 'Valid', preview: documentPreview },
            { id: 2, title: 'Insurance Certificate', status: 'Valid', preview: documentPreview },
        ],
        showRequestInformation: true,
        showAddDocument: true,
        addDocumentLabel: 'Carrier to provide Director ID / Company extract',
    },
};

const applyDemoSummaryDates = (summary, bid) => {
    const submittedAt = bid.submittedAt
        || bid.createdAt
        || formatDemoDateTime(parseDemoDateTime());
    const submittedDate = parseDemoDateTime(submittedAt);

    const enrichedSummary = {
        ...summary,
        submittedAt,
    };

    if (summary.duration) {
        enrichedSummary.completedAt = formatDemoDateTime(addDemoMinutes(
            submittedDate,
            summary.duration.includes('15') ? 15 : 13
        ));
    }

    if (Object.prototype.hasOwnProperty.call(summary, 'estimatedCompletion')) {
        enrichedSummary.lastUpdate = formatDemoDateTime(addDemoMinutes(submittedDate, 6));
    }

    return enrichedSummary;
};

const defaultSummaryByStatus = {
    Verified: bidSummaries[1],
    Rejected: bidSummaries[2],
    'Action Required': bidSummaries[3],
    Verifying: {
        country: 'NL',
        established: 2016,
        deliveryDays: 2,
        estimatedCompletion: '~ 8 minutes',
        outcome: {
            status: 'Verifying',
            description: 'Automatic verification is currently in progress for this carrier bid. Company, document, and identity checks are still running.',
            metrics: [
                { label: 'Risk level', value: 'Medium', type: 'risk' },
                { label: 'Completed checks', value: '1 / 5' },
                { label: 'Outstanding items', value: 4 },
                { label: 'Recommendation', value: 'Wait for completion' },
            ],
        },
        steps: verificationSteps.verifying,
        highlightsTitle: 'Checks in progress',
        attentionItems: [],
        otherChecks: [
            { id: 1, title: 'Documents received', description: 'Submitted documents are being reviewed automatically.', status: 'Verified', icon: 'ri-file-text-line' },
            { id: 2, title: 'Company data', description: 'Company registry checks are still running.', status: 'Needs info', icon: 'ri-building-line' },
        ],
        documents: [
            { id: 1, title: 'Carrier License', status: 'Valid', preview: documentPreview },
        ],
        showRequestInformation: false,
        showAddDocument: false,
    },
};

class CarrierGuardBidDetailsService {
    static getInstance() {
        return new CarrierGuardBidDetailsService();
    }

    async fetchDetails(freightId, bidId) {
        const freightService = CarrierGuardFreightDetailsService.getInstance();
        const freight = await freightService.fetchDetails(freightId);

        if (!freight) return null;

        const bid = freight.bids.find((item) => String(item.id) === String(bidId));

        if (!bid) return null;

        const summary = applyDemoSummaryDates(
            bidSummaries[bid.id] || defaultSummaryByStatus[bid.status],
            bid
        );
        const carrier = getEuropeanCarrierById(bid.carrierId);
        const publicProfile = getCarrierPublicProfile(bid.carrierId);
        const primaryContact = carrier.contacts.find((contact) => contact.email);
        const company = {
            legalName: publicProfile?.legalName || carrier.name,
            country: `${carrier.countryName} (${carrier.country})`,
            established: publicProfile?.established || '—',
            address: publicProfile?.address || `${carrier.headquarters}, ${carrier.countryName}`,
            website: publicProfile?.website || carrier.website,
            email: publicProfile?.email || primaryContact?.email || '—',
            phone: publicProfile?.phone || '—',
        };
        const companyReports = createCompanyReports(carrier, publicProfile);

        return {
            ...bid,
            ...summary,
            country: carrier.country,
            established: publicProfile?.established || '—',
            freightId: freight.id,
            freightReference: freight.reference,
            freight: {
                cargoType: freight.cargo.type,
                destination: `${freight.destination.city}, ${freight.destination.country}`,
                origin: `${freight.origin.city}, ${freight.origin.country}`,
                pickupDate: freight.pickupDate,
                weight: freight.cargo.weight,
            },
            company,
            companyReports,
            documentForensics: createDocumentForensics({
                ...summary,
                company,
                companyReports,
            }),
            callVerification: createCallVerification({
                company,
                country: carrier.country,
                freightReference: freight.reference,
                status: summary.outcome.status,
                submittedAt: summary.submittedAt,
            }),
        };
    }
}

export default CarrierGuardBidDetailsService;
